import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NotificationChannel, NotificationTemplateItem } from 'src/app/models/NotificationConfig';
import { NotificationType, NotificationTypeLabels } from 'src/app/models/NotificationType';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { LanguageService } from '../../shared/services/language.service';
import { NotificationsService } from '../../shared/services/notifications.service';
import { NotificationUserConfiguration } from 'src/app/models/NotificationUserConfiguration';

@Component({
  standalone: false,
  selector: 'app-notification-config',
  templateUrl: './notification-config.component.html',
  styleUrls: ['./notification-config.component.css']
})
export class NotificationConfigComponent implements OnInit, OnDestroy {

  variables: any;
  isLoading = false;
  errorDetected = false;
  errorText = '';
  configs: NotificationUserConfiguration[] = [];
  readonly noticeChannel = NotificationChannel.NOTICE;
  readonly emailChannel = NotificationChannel.EMAIL;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private languageService: LanguageService,
    private notificationsService: NotificationsService,
    private infoBar: InfoBarWithStatusService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.languageService.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vars: any) => {
        this.variables = vars;
      });

    this.loadConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveConfig(): void {
    this.configs.forEach(config => {
      if (config.configurableChannels.length != 0 ) {

        if (config.configurableChannels[0] == null)
          config.configurableChannels[0] = NotificationChannel.NONE;
        if (config.configurableChannels[1] == null)
          config.configurableChannels[1] = NotificationChannel.NONE;
      }
    });

    this.notificationsService.updateNotificationUserConfiguration(this.configs).subscribe({
      next: () => {
        this.infoBar.showSnackbar(this.variables?.notifConfigSaved || 'Preferencias de notificación guardadas con éxito.', ['successful-snackbar'], 3000);
      },
      error: () => {
        this.infoBar.showSnackbar(this.variables?.notifConfigError || 'Error al guardar las preferencias de notificación.', ['error-snackbar'], 3500);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/notifications']);
  }

  isChannelConfigurable(row: NotificationTemplateItem, channel: NotificationChannel): boolean {
    return (row.configurableChannels?.includes(channel) ?? false) || row.configurableChannels?.length === 0
  }

  isChannelDisabled(row: NotificationTemplateItem, channel: NotificationChannel): boolean {
    return row.configurableChannels?.length == 0;
  }

  onConfigurableChannelToggle(row: NotificationTemplateItem, channel: NotificationChannel, checked: boolean): void {
    const index = this.getChannelIndex(channel);
    
    if (!row.configurableChannels) {
      row.configurableChannels = [];
    }

    row.configurableChannels[index] = checked ? channel : null;
  }

  getTypeMeta(eventType: string | number): { code: string; label: string; icon: string } | undefined {
    if (typeof eventType === 'number') {
      return NotificationTypeLabels[eventType as NotificationType];
    }
    return Object.values(NotificationTypeLabels).find(meta => meta.code === String(eventType));
  }

  getTranslatedTypeLabel(typeCode: string, fallbackLabel: string): string {
    const keyByCode: Record<string, string> = {
      ESTIMATION_TO_CERTIFICATE: 'notificationTypePendingCertification',
      REVIEW_ADDED: 'notificationTypeReviewAdded',
      ESTIMATION_APPROVED: 'notificationTypeApproved',
      ESTIMATION_REJECTED: 'notificationTypeRejected',
      ESTIMATION_TO_FIX: 'notificationTypeToFix'
    };
    const translationKey = keyByCode[typeCode];
    return translationKey ? (this.variables?.[translationKey] || fallbackLabel) : fallbackLabel;
  }

  private getChannelIndex(channel: NotificationChannel): number {
    return channel === NotificationChannel.NOTICE ? 0 : 1;
  }

  private loadConfig(): void {
    this.isLoading = true;
    this.errorDetected = false;

    this.notificationsService.getNotificationUserConfiguration().subscribe({
      next: (data: NotificationUserConfiguration[]) => {
        this.configs = data || [];
        this.isLoading = false;
      },
      error: () => {
        this.errorDetected = true;
        this.isLoading = false;
      }
    });
  }
}
